// Niveau 6 — Flydighed og lange tekster

import type { CurriculumTier, Trin } from '../../types';
import { NIVEAU_1_SPRINT_WORDS } from './niveau-1';
import { NIVEAU_2_SPRINT_WORDS } from './niveau-2';
import { NIVEAU_3_SPRINT_WORDS } from './niveau-3';
import { NIVEAU_4_ALPHABET_WORDS } from './niveau-4';
import {
  NIVEAU_5_MIXED_WORDS,
  NIVEAU_5_SPRINT_WORDS,
  NIVEAU_5_UNLOCKED_KEYS,
} from './niveau-5';
import {
  filterWords,
  fromChunks,
  interleaved,
  literal,
  round,
} from './generators';

const prior = [...NIVEAU_5_UNLOCKED_KEYS];

const PARA_1 =
  'Jeg kan skrive længere tekster nu. Det føles godt at bruge hele tastaturet.';
const PARA_2 =
  'Mor laver mad i køkkenet. Far læser en bog. Vi skal snart gå en tur.';
const PARA_FINALE =
  'Jeg har lært blindskrift på skolen. Det tager tid at blive god. Men øvelse gør mig stærkere hver dag. Skriv videre!';

const t1: Trin = {
  id: 'niveau-6-trin-1',
  tierId: 'niveau-6',
  trinNumber: 1,
  title: 'Længere sætninger',
  description: 'To sætninger i træk med korrekt case.',
  goal: 'Hold fokus over flere sætninger uden at miste præcision.',
  unlockedKeys: prior,
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-6-trin-1-r1',
      title: 'To sætninger',
      purpose: 'Rolig start.',
      mode: 'guided',
      generate: literal(PARA_1),
    }),
    round({
      id: 'niveau-6-trin-1-r2',
      title: 'Genkald',
      purpose: 'Samme længde uden ledetråd.',
      mode: 'recall',
      generate: literal(PARA_2),
    }),
    round({
      id: 'niveau-6-trin-1-r3',
      title: 'Hold takten',
      purpose: 'Jævnt tempo gennem teksten.',
      mode: 'recall',
      generate: literal(PARA_1),
      presentation: { kind: 'metronome', bpm: 60 },
    }),
    round({
      id: 'niveau-6-trin-1-r4',
      title: 'Ord-pool',
      purpose: 'Varierede ord med case.',
      mode: 'recall',
      generate: fromChunks(NIVEAU_5_SPRINT_WORDS, 100),
    }),
    round({
      id: 'niveau-6-trin-1-r5',
      title: 'Mini-udfordring',
      purpose: 'Uden hjælp.',
      mode: 'challenge',
      generate: literal(PARA_2),
    }),
  ],
};

const t2: Trin = {
  id: 'niveau-6-trin-2',
  tierId: 'niveau-6',
  trinNumber: 2,
  title: 'Afsnit',
  description: 'Flere sætninger som én sammenhængende tekst.',
  goal: 'Se hele afsnittet kort — skriv det fra hukommelsen.',
  unlockedKeys: prior,
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-6-trin-2-r1',
      title: 'Læs afsnittet',
      purpose: 'Tre sætninger i ét flow.',
      mode: 'guided',
      generate: literal(PARA_2),
    }),
    round({
      id: 'niveau-6-trin-2-r2',
      title: 'Flash-afsnit',
      purpose: 'Kort visning af hele teksten.',
      mode: 'recall',
      generate: literal(PARA_1),
      presentation: { kind: 'echo', flashMs: 2800, unit: 'chunk' },
    }),
    round({
      id: 'niveau-6-trin-2-r3',
      title: 'Flash igen',
      purpose: 'Længere afsnit.',
      mode: 'recall',
      generate: literal(PARA_2),
      presentation: { kind: 'echo', flashMs: 3200, unit: 'chunk' },
    }),
    round({
      id: 'niveau-6-trin-2-r4',
      title: 'Blanding',
      purpose: 'Ord og korte sætninger.',
      mode: 'recall',
      generate: fromChunks(NIVEAU_5_MIXED_WORDS, 110),
    }),
    round({
      id: 'niveau-6-trin-2-r5',
      title: 'Mini-udfordring',
      purpose: 'Uden at se teksten undervejs.',
      mode: 'challenge',
      generate: literal(PARA_1),
    }),
  ],
};

const t3: Trin = {
  id: 'niveau-6-trin-3',
  tierId: 'niveau-6',
  trinNumber: 3,
  title: 'Tempo',
  description: 'Skygge-runde med højere hastighed.',
  goal: 'Følg teksten der ruller — hold rytmen.',
  unlockedKeys: prior,
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-6-trin-3-r1',
      title: 'Opvarmning',
      purpose: 'Rolig skygge.',
      mode: 'guided',
      generate: fromChunks(NIVEAU_5_SPRINT_WORDS, 80),
      presentation: { kind: 'shadow', cps: 5 },
    }),
    round({
      id: 'niveau-6-trin-3-r2',
      title: 'Skygge sætning',
      purpose: 'Én sætning i tempo.',
      mode: 'recall',
      generate: literal(PARA_1),
      presentation: { kind: 'shadow', cps: 5 },
    }),
    round({
      id: 'niveau-6-trin-3-r3',
      title: 'Hurtigere',
      purpose: 'Skygge med højere cps.',
      mode: 'recall',
      generate: literal(PARA_2),
      presentation: { kind: 'shadow', cps: 6 },
    }),
    round({
      id: 'niveau-6-trin-3-r4',
      title: 'Ord-flow',
      purpose: 'Varieret ordforråd i tempo.',
      mode: 'recall',
      generate: fromChunks(NIVEAU_5_MIXED_WORDS, 100),
      presentation: { kind: 'shadow', cps: 5 },
    }),
    round({
      id: 'niveau-6-trin-3-r5',
      title: 'Udfordring',
      purpose: 'Hurtig skygge.',
      mode: 'challenge',
      generate: fromChunks(NIVEAU_5_SPRINT_WORDS, 90),
      presentation: { kind: 'shadow', cps: 6 },
    }),
  ],
};

const t4: Trin = {
  id: 'niveau-6-trin-4',
  tierId: 'niveau-6',
  trinNumber: 4,
  title: 'Afsnit-diktat',
  description: 'Lyt til hele afsnittet — skriv uden at se.',
  goal: 'Forstå sammenhængen i lyd — skriv præcist.',
  unlockedKeys: prior,
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-6-trin-4-r1',
      title: 'Kort afsnit',
      purpose: 'To sætninger læses én gang.',
      mode: 'recall',
      generate: literal(PARA_1),
      presentation: {
        kind: 'dictation',
        dictationMode: 'paragraph',
        sentencePauseMs: 700,
      },
    }),
    round({
      id: 'niveau-6-trin-4-r2',
      title: 'Længere afsnit',
      purpose: 'Tre sætninger i ét diktat.',
      mode: 'recall',
      generate: literal(PARA_2),
      presentation: {
        kind: 'dictation',
        dictationMode: 'paragraph',
        sentencePauseMs: 800,
      },
    }),
    round({
      id: 'niveau-6-trin-4-r3',
      title: 'Ord-genkald',
      purpose: 'Pause mellem diktat og flow.',
      mode: 'recall',
      generate: fromChunks(NIVEAU_5_MIXED_WORDS, 80),
    }),
    round({
      id: 'niveau-6-trin-4-r4',
      title: 'Sætning synlig',
      purpose: 'Skriv med tekst foran dig.',
      mode: 'recall',
      generate: literal(PARA_1),
    }),
    round({
      id: 'niveau-6-trin-4-r5',
      title: 'Mini-udfordring',
      purpose: 'Diktat uden hjælp.',
      mode: 'challenge',
      generate: literal(PARA_2),
      presentation: {
        kind: 'dictation',
        dictationMode: 'paragraph',
        sentencePauseMs: 600,
      },
    }),
  ],
};

const t5: Trin = {
  id: 'niveau-6-trin-5',
  tierId: 'niveau-6',
  trinNumber: 5,
  title: 'Fuld blindskrift',
  description: 'Afsluttende udfordring med lange tekster.',
  goal: 'Bevis at du kan skrive flydende dansk med hele tastaturet.',
  unlockedKeys: prior,
  newKeys: [],
  strictCase: true,
  rounds: [
    round({
      id: 'niveau-6-trin-5-r1',
      title: 'Opvarmning',
      purpose: 'Rolig start.',
      mode: 'guided',
      generate: fromChunks(NIVEAU_5_SPRINT_WORDS, 70),
    }),
    round({
      id: 'niveau-6-trin-5-r2',
      title: 'Bland alle niveauer',
      purpose: 'Friskhed fra hele pensum.',
      mode: 'recall',
      generate: interleaved(
        [
          { chunks: NIVEAU_1_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_2_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_3_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_4_ALPHABET_WORDS, weight: 1 },
          { chunks: NIVEAU_5_MIXED_WORDS, weight: 2 },
        ],
        110,
      ),
    }),
    round({
      id: 'niveau-6-trin-5-r3',
      title: 'Lang tekst',
      purpose: 'Sammenhængende afsnit.',
      mode: 'recall',
      generate: literal(PARA_FINALE),
    }),
    round({
      id: 'niveau-6-trin-5-r4',
      title: 'Afsnit-diktat',
      purpose: 'Afsluttende lytte-udfordring.',
      mode: 'challenge',
      generate: literal(PARA_FINALE),
      presentation: {
        kind: 'dictation',
        dictationMode: 'paragraph',
        sentencePauseMs: 900,
      },
    }),
    round({
      id: 'niveau-6-trin-5-r5',
      title: 'Skygge-finale',
      purpose: 'Følg teksten til målstregen.',
      mode: 'challenge',
      generate: fromChunks(NIVEAU_5_MIXED_WORDS, 100),
      presentation: { kind: 'shadow', cps: 6 },
    }),
  ],
};

export const NIVEAU_6_UNLOCKED_KEYS = prior;

export const NIVEAU_6_SPRINT_WORDS = filterWords(
  [
    'Jeg', 'Det', 'Vi', 'kan', 'har', 'skole', 'mor', 'far', 'skrive', 'læse',
    'Danmark', 'familie', 'arbejde', 'verden', 'tekst', 'sætning', 'finger',
    'blindskrift', 'øvelse', 'dag', 'god', 'stor', 'lille',
  ],
  prior,
);

export const NIVEAU_6_TRINS: Trin[] = [t1, t2, t3, t4, t5];

export const NIVEAU_6_TIER: CurriculumTier = {
  id: 'niveau-6',
  title: 'Niveau 6',
  description: 'Flydighed, fart og lange tekster.',
  trins: NIVEAU_6_TRINS,
};
